import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { api } from "../lib/api.js";

export default function SatisfactionDashboardPage() {
  const [data, setData] = useState([]);
  const [groupBy, setGroupBy] = useState("department");
  const [months, setMonths] = useState(6);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/surveys-trends?group_by=${groupBy}&months=${months}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [groupBy, months]);

  // Compute global aggregates (across all groups)
  const globalMonths = {};
  data.forEach((g) => g.months.forEach((m) => {
    const b = globalMonths[m.month] = globalMonths[m.month] || { nps: [], csi: [], ces: [], responses: 0 };
    b.responses += m.responses || 0;
    if (m.nps_score != null) b.nps.push({ score: m.nps_score, n: m.nps_responses });
    if (m.csi_avg != null) b.csi.push({ score: m.csi_avg, n: m.csi_responses });
    if (m.ces_avg != null) b.ces.push({ score: m.ces_avg, n: m.ces_responses });
  }));
  const globalSeries = Object.keys(globalMonths).sort().map((k) => {
    const b = globalMonths[k];
    const weightedAvg = (arr) => {
      if (!arr.length) return null;
      const totalN = arr.reduce((s, x) => s + x.n, 0);
      if (!totalN) return null;
      return arr.reduce((s, x) => s + x.score * x.n, 0) / totalN;
    };
    return {
      month: k,
      responses: b.responses,
      nps_score: weightedAvg(b.nps),
      csi_avg: weightedAvg(b.csi),
      ces_avg: weightedAvg(b.ces),
    };
  });

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1A1A2E] flex items-center gap-2">
            <Star className="w-6 h-6 text-[#1F3864]" /> Dashboard de Satisfação
          </h1>
          <p className="text-sm text-[#6C757D] mt-1">Tendências mensais NPS/CSI/CES por {groupBy === "department" ? "departamento" : groupBy === "brand" ? "marca" : "dealer"}.</p>
        </div>
        <div className="flex gap-2">
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="text-xs border border-gray-300 rounded-sm px-2 py-1.5" data-testid="sat-group-by">
            <option value="department">Por departamento</option>
            <option value="brand">Por marca</option>
            <option value="branch">Por dealer</option>
          </select>
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="text-xs border border-gray-300 rounded-sm px-2 py-1.5" data-testid="sat-months">
            <option value={3}>3 meses</option>
            <option value={6}>6 meses</option>
            <option value={12}>12 meses</option>
          </select>
        </div>
      </div>

      {loading && <div className="text-sm text-[#6C757D] italic">A carregar…</div>}

      {/* Global aggregates */}
      <div className="bg-white border border-gray-200 rounded-md p-4 mb-6" data-testid="sat-global-card">
        <div className="font-heading text-sm font-bold text-[#1F3864] mb-3 uppercase tracking-widest">Global · últimos {months} meses</div>
        {globalSeries.length === 0 ? (
          <div className="text-sm text-[#6C757D] italic text-center py-6">Sem respostas no período.</div>
        ) : (
          <div className="grid grid-cols-3 gap-6">
            <MetricCard title="NPS" series={globalSeries} field="nps_score" min={-100} max={100} formatter={(v) => Math.round(v)} color="#1F3864" />
            <MetricCard title="CSI (1-5)" series={globalSeries} field="csi_avg" min={0} max={5} formatter={(v) => v.toFixed(2)} color="#28A745" />
            <MetricCard title="CES (1-5)" series={globalSeries} field="ces_avg" min={0} max={5} formatter={(v) => v.toFixed(2)} color="#DC7633" />
          </div>
        )}
      </div>

      {/* Per-group breakdown */}
      {data.map((g) => (
        <div key={g.group} className="bg-white border border-gray-200 rounded-md p-4 mb-4" data-testid={`sat-group-${g.group}`}>
          <div className="font-heading text-sm font-bold text-[#1A1A2E] mb-3">{g.group}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 text-[#6C757D] uppercase tracking-widest text-[10px]">
                  <th className="text-left px-2 py-1">Mês</th>
                  <th className="text-right px-2 py-1">Respostas</th>
                  <th className="text-right px-2 py-1">NPS</th>
                  <th className="text-right px-2 py-1">CSI</th>
                  <th className="text-right px-2 py-1">CES</th>
                </tr>
              </thead>
              <tbody>
                {g.months.map((m) => (
                  <tr key={m.month} className="border-b border-gray-100">
                    <td className="px-2 py-1 font-mono">{m.month}</td>
                    <td className="px-2 py-1 text-right">{m.responses}</td>
                    <td className={`px-2 py-1 text-right font-semibold ${m.nps_score != null && m.nps_score < 0 ? "text-[#DC3545]" : m.nps_score != null && m.nps_score < 30 ? "text-[#DC7633]" : "text-[#28A745]"}`}>{m.nps_score != null ? Math.round(m.nps_score) : "—"}</td>
                    <td className={`px-2 py-1 text-right font-semibold ${m.csi_avg != null && m.csi_avg < 3.5 ? "text-[#DC3545]" : "text-[#1A1A2E]"}`}>{m.csi_avg != null ? m.csi_avg.toFixed(2) : "—"}</td>
                    <td className={`px-2 py-1 text-right font-semibold ${m.ces_avg != null && m.ces_avg < 3.5 ? "text-[#DC3545]" : "text-[#1A1A2E]"}`}>{m.ces_avg != null ? m.ces_avg.toFixed(2) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {!loading && data.length === 0 && <div className="text-sm text-[#6C757D] italic text-center bg-white border border-gray-200 rounded-md p-6">Sem dados no período seleccionado.</div>}
    </div>
  );
}

function MetricCard({ title, series, field, min, max, formatter, color }) {
  const values = series.map((s) => s[field]).filter((v) => v != null);
  const latest = values.length ? values[values.length - 1] : null;
  const prev = values.length > 1 ? values[values.length - 2] : null;
  const delta = latest != null && prev != null ? latest - prev : null;
  // Mini sparkline SVG
  const w = 220, h = 48;
  const n = series.length || 1;
  const stepX = w / Math.max(1, n - 1);
  const toY = (v) => v == null ? null : h - ((v - min) / (max - min)) * h;
  const points = series.map((s, i) => {
    const y = toY(s[field]);
    return y == null ? null : `${i * stepX},${y.toFixed(1)}`;
  }).filter(Boolean).join(" ");
  return (
    <div data-testid={`sat-metric-${title}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">{title}</div>
      <div className="flex items-baseline gap-2">
        <div className="font-heading text-3xl font-bold" style={{ color }}>{latest != null ? formatter(latest) : "—"}</div>
        {delta != null && (
          <span className={`text-xs font-semibold ${delta >= 0 ? "text-[#28A745]" : "text-[#DC3545]"}`}>{delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}</span>
        )}
      </div>
      <svg width={w} height={h} className="mt-2 overflow-visible">
        {points && <polyline points={points} fill="none" stroke={color} strokeWidth="2" />}
        {series.map((s, i) => {
          const y = toY(s[field]);
          if (y == null) return null;
          return <circle key={i} cx={i * stepX} cy={y} r="2.5" fill={color} />;
        })}
      </svg>
    </div>
  );
}
